import {
  trackCommentAdded,
  trackCommentDeleted,
  trackCommentDrawingAdded,
  trackCommentDrawingCleared,
  trackCommentDrawingEdited,
  trackCommentEdited,
  trackCommentMoved,
  trackCommentReactionAdded,
  trackCommentReplyAdded,
  trackCommentResolved,
  trackCommentUnresolved,
} from '@src/analytics/EventTracker';
import { inkName } from '@src/utility/annotationInk';
import { sanitizeDrawing } from '@src/utility/commentDrawing';
import { parseMentionIds } from '@src/utility/commentMentions';
import { toggleReaction } from '@src/utility/comments';
import { LogManager } from '@src/utility/Logger';
import { makeAutoObservable, observable } from 'mobx';

import type { CommentMoveMethod } from '@src/analytics/EventTracker';
import type { CommentService } from '@src/services/CommentService';
import type { ServiceError } from '@src/services/common/Result';
import type { Comment, Drawing } from '@src/types.ts/comment';

const logger = LogManager.getLogger('store:CommentStore');

/** Refetch cadence for the polling host. The live-refresh ticket tunes this. */
const POLL_INTERVAL_MS = 15_000;

/** Author identity for the authenticated editor (never a guest here). */
export interface CommentAuthor {
  id: string | null;
  name: string;
  email: string | null;
  /** Org/workspace role (Clerk or Dato), for analytics; may be undefined. */
  role?: string;
}

/** A top-level comment plus its replies (a derived view, not stored). */
export interface CommentThread {
  top: Comment;
  replies: Comment[];
  /** A thread is resolved iff its top-level comment has `resolvedAt`. */
  isResolved: boolean;
}

export interface CommentStoreOptions {
  service: CommentService;
  author: CommentAuthor;
  /** DatoCMS Video record id of the variant whose thread this store holds. */
  videoId?: string;
  /** Parent Story record id, for analytics enrichment. */
  storyId?: string;
}

/**
 * Observable, clip-scoped session state for the open clip's comment threads —
 * the seam between `CommentService` and the Content Studio clip-preview UI.
 *
 * Owns optimistic post/reply/edit/resolve/react/delete (with rollback on a
 * service error) and hosts the polling cadence. Not a `RootStore` singleton:
 * like `ClipEditSessionStore`, it lives for the open clip and is created via
 * `useCommentStore`. `CommentService` and the author identity are
 * constructor-injected — the store imports no React/hooks (layer boundary).
 */
export class CommentStore {
  private readonly service: CommentService;
  private author: CommentAuthor;

  private videoId?: string;
  private storyId?: string;

  /**
   * Flat comment list (top-level + replies). Held as `observable.ref` and
   * replaced wholesale on every load/mutation so nested `Comment` objects stay
   * shallow (per CLAUDE.md MobX rules). `threads` derives the view.
   */
  private _comments: Comment[] = [];
  private _isLoading = false;
  private _lastError: ServiceError | null = null;

  private pollTimer: ReturnType<typeof setInterval> | null = null;
  /** Monotonic counter for optimistic temp ids (deterministic for tests). */
  private tempIdSeq = 0;

  /** In-flight optimistic mutations; a background poll skips while this is > 0. */
  private _activeMutations = 0;
  /**
   * Bumped at the start of every mutation. A poll captures it before fetching
   * and discards the result if it changed — catching a mutation that started
   * (and even finished) while the poll's request was in flight.
   */
  private _mutationGeneration = 0;

  constructor(opts: CommentStoreOptions) {
    this.service = opts.service;
    this.author = opts.author;
    this.videoId = opts.videoId;
    this.storyId = opts.storyId;

    makeAutoObservable(this, {
      // Injected deps are not observable state.
      service: false,
      author: false,
      // Replaced wholesale on every load/mutation — never deeply observed.
      _comments: observable.ref,
      // Internal race-guard bookkeeping, not reactive UI state.
      _activeMutations: false,
      _mutationGeneration: false,
      // Cast mirrors ClipEditSessionStore: MobX's AnnotationsMap omits private
      // fields, so annotating `_comments` needs the assertion.
    } as any);

    if (this.videoId) {
      void this.refresh();
    }
  }

  // ---- Derived views ------------------------------------------------------

  /** Top-level comments (oldest-first) each with their replies (oldest-first). */
  get threads(): CommentThread[] {
    const repliesByParent = new Map<string, Comment[]>();
    const topLevel: Comment[] = [];

    for (const comment of this._comments) {
      if (comment.parentId == null) {
        topLevel.push(comment);
      } else {
        const bucket = repliesByParent.get(comment.parentId);
        if (bucket) bucket.push(comment);
        else repliesByParent.set(comment.parentId, [comment]);
      }
    }

    topLevel.sort(byCreatedAtAsc);
    return topLevel.map(top => {
      const replies = repliesByParent.get(top.id) ?? [];
      replies.sort(byCreatedAtAsc);
      return { top, replies, isResolved: top.resolvedAt != null };
    });
  }

  /**
   * Flat count of all comments (top-level + replies) for the current video.
   * Drives the clips-list card indicator so a post/delete reflects immediately
   * (the list otherwise only refetches on modal close).
   */
  get commentCount(): number {
    return this._comments.length;
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  get lastError(): ServiceError | null {
    return this._lastError;
  }

  /**
   * Whether `comment` was authored by the current editor. Deletion is
   * owner-only, so the UI gates the trash affordance on this. Matches the
   * reaction identity (`authorId`, falling back to `authorName`).
   */
  isOwnComment(comment: Comment): boolean {
    return (comment.authorId ?? comment.authorName) === this.authorIdentifier;
  }

  /** Whether the current author has reacted to `comment` with `emoji`. */
  hasReacted(comment: Comment, emoji: string): boolean {
    return (comment.reactions[emoji] ?? []).includes(this.authorIdentifier);
  }

  // ---- Loading ------------------------------------------------------------

  // Update the current author identity (e.g. after auth context loads).
  setAuthor(author: CommentAuthor): void {
    this.author = author;
  }

  /**
   * Re-key to a different Video record and reload. Each AR format is its own
   * record, so switching dimension switches the keyed video. Pass `undefined`
   * (e.g. an unsaved variant) to clear the view and skip loading.
   */
  setClip(videoId?: string): void {
    if (videoId === this.videoId) return;
    this.videoId = videoId;
    this._comments = [];
    void this.refresh();
  }

  /** Refetch threads for the current video. No-op for an unsaved variant. */
  async refresh(): Promise<void> {
    if (!this.videoId) return;
    const videoId = this.videoId;
    this._isLoading = true;
    this._lastError = null;
    const result = await this.service.listByClip(videoId);
    // Discard if we re-keyed while the request was in flight (out-of-order
    // resolve on rapid AR switching); the latest refresh owns the state.
    if (this.videoId !== videoId) return;
    this._isLoading = false;
    if (result.ok) {
      this._comments = result.value;
    } else {
      this._lastError = result.error;
    }
  }

  // ---- Optimistic mutations -----------------------------------------------

  /**
   * Post a top-level comment, pinned to `videoTimeSeconds` (or general),
   * optionally carrying the marks drawn on that frame (PLAT-11290).
   */
  async addComment(
    body: string,
    videoTimeSeconds: number | null,
    drawing?: Drawing | null,
  ): Promise<void> {
    await this.createOptimistic(body, videoTimeSeconds, null, drawing);
  }

  /** Post a reply under an existing top-level comment. */
  async reply(parentId: string, body: string): Promise<void> {
    await this.createOptimistic(body, null, parentId);
  }

  private async createOptimistic(
    body: string,
    videoTimeSeconds: number | null,
    parentId: string | null,
    drawing?: Drawing | null,
  ): Promise<void> {
    if (!this.videoId) return;
    this.beginMutation();
    const previous = this._comments;
    this._lastError = null;
    const tempId = `temp-${this.tempIdSeq++}`;
    const optimistic: Comment = {
      id: tempId,
      contentType: 'clip',
      videoId: this.videoId,
      videoTimeSeconds,
      body,
      authorId: this.author.id,
      authorName: this.author.name,
      authorEmail: this.author.email,
      isGuest: false,
      parentId,
      resolvedAt: null,
      reactions: {},
      mentions: parseMentionIds(body),
      // Sanitized here too, so the optimistic row is exactly what the server
      // will store — otherwise a rejected stroke would show for a moment and
      // then disappear on the next poll.
      drawing: sanitizeDrawing(drawing),
      createdAt: new Date().toISOString(),
      editedAt: null,
    };
    this._comments = [...previous, optimistic];

    const result = await this.service.createComment({
      contentType: 'clip',
      videoId: this.videoId,
      body,
      videoTimeSeconds,
      authorId: this.author.id,
      authorName: this.author.name,
      authorEmail: this.author.email,
      isGuest: false,
      parentId,
      // Body and marks go in a single write, so a comment is never posted with
      // a half-saved drawing.
      drawing,
    });

    if (result.ok) {
      const newId = result.value.id;
      this._comments = this._comments.map(c =>
        c.id === tempId ? { ...c, id: newId } : c,
      );
      if (parentId == null) {
        trackCommentAdded({
          ...this.eventBase,
          is_pinned: videoTimeSeconds != null,
          is_guest: optimistic.isGuest,
          author_role: this.author.role,
        });
        // Companion event when the comment carried marks (PLAT-11296). Reads
        // the optimistic row so it reports exactly what was stored — the
        // sanitized strokes, not the raw input. Tool mix / ink / count only;
        // never the points.
        if (optimistic.drawing) {
          const strokes = optimistic.drawing.strokes;
          trackCommentDrawingAdded({
            ...this.eventBase,
            is_guest: optimistic.isGuest,
            author_role: this.author.role,
            tools: [...new Set(strokes.map(stroke => stroke.tool))],
            inks: [...new Set(strokes.map(stroke => inkName(stroke.color)))],
            stroke_count: strokes.length,
          });
        }
      } else {
        trackCommentReplyAdded({
          ...this.eventBase,
          is_guest: optimistic.isGuest,
          author_role: this.author.role,
        });
      }
    } else {
      this.rollback(previous, result.error, 'create comment');
    }
    this.endMutation();
  }

  /** Edit a comment's body. */
  async editComment(id: string, body: string): Promise<void> {
    this.beginMutation();
    const previous = this._comments;
    this._lastError = null;
    this._comments = previous.map(c =>
      c.id === id
        ? {
            ...c,
            body,
            mentions: parseMentionIds(body),
            editedAt: new Date().toISOString(),
          }
        : c,
    );

    const result = await this.service.editComment(id, body);
    if (!result.ok) this.rollback(previous, result.error, 'edit comment');
    else trackCommentEdited({ ...this.eventBase });
    this.endMutation();
  }

  /**
   * Replace the marks on a comment, or clear them with `null` (PLAT-11292).
   *
   * **No UI calls this yet.** The text-edit form used to, but editing a comment
   * is for its words — mark editing needs its own affordance, which PLAT-11292
   * still owes. Kept because the capability is wanted and this layer is tested;
   * delete it if that ticket is dropped rather than letting it rot.
   *
   * Counts as an edit — `editedAt` is stamped, matching the repository —
   * because redrawing changes what the feedback points at. Optimistic like the
   * other mutations, and sanitized locally so the row shown is what will be
   * stored.
   */
  async setCommentDrawing(id: string, drawing: Drawing | null): Promise<void> {
    const target = this._comments.find(c => c.id === id);
    if (!target) return;
    const next = sanitizeDrawing(drawing);

    this.beginMutation();
    const previous = this._comments;
    this._lastError = null;
    this._comments = previous.map(c =>
      c.id === id
        ? { ...c, drawing: next, editedAt: new Date().toISOString() }
        : c,
    );

    const result = await this.service.updateDrawing(id, next);
    if (!result.ok) {
      this.rollback(previous, result.error, 'update comment drawing');
    } else if (next) {
      const strokes = next.strokes;
      trackCommentDrawingEdited({
        ...this.eventBase,
        tools: [...new Set(strokes.map(stroke => stroke.tool))],
        inks: [...new Set(strokes.map(stroke => inkName(stroke.color)))],
        stroke_count: strokes.length,
      });
    } else {
      trackCommentDrawingCleared({ ...this.eventBase });
    }
    this.endMutation();
  }

  /**
   * Move a comment to a different playhead position, or unpin it with `null`.
   * `method` records how the user did it — dragging the scrubber marker vs
   * typing a timecode — which are separate affordances worth comparing.
   *
   * Not an edit: `editedAt` is left alone, matching the repository.
   */
  async setCommentTime(
    id: string,
    videoTimeSeconds: number | null,
    method: CommentMoveMethod,
  ): Promise<void> {
    const target = this._comments.find(c => c.id === id);
    if (!target || target.videoTimeSeconds === videoTimeSeconds) return;
    this.beginMutation();
    const previous = this._comments;
    this._lastError = null;
    this._comments = previous.map(c =>
      c.id === id ? { ...c, videoTimeSeconds } : c,
    );

    const result = await this.service.moveComment(id, videoTimeSeconds);
    if (!result.ok) this.rollback(previous, result.error, 'move comment');
    else {
      trackCommentMoved({
        ...this.eventBase,
        method,
        from_seconds: target.videoTimeSeconds,
        to_seconds: videoTimeSeconds,
      });
    }
    this.endMutation();
  }

  /** Resolve or unresolve a thread (stamps/clears the top-level `resolvedAt`). */
  async setResolved(id: string, resolved: boolean): Promise<void> {
    this.beginMutation();
    const previous = this._comments;
    this._lastError = null;
    this._comments = previous.map(c =>
      c.id === id
        ? { ...c, resolvedAt: resolved ? new Date().toISOString() : null }
        : c,
    );

    const result = await this.service.setResolved(id, resolved);
    if (!result.ok) this.rollback(previous, result.error, 'resolve comment');
    else if (resolved) trackCommentResolved({ ...this.eventBase });
    else trackCommentUnresolved({ ...this.eventBase });
    this.endMutation();
  }

  /** Toggle the current author's reaction with `emoji` on a comment. */
  async toggleReaction(id: string, emoji: string): Promise<void> {
    const target = this._comments.find(c => c.id === id);
    if (!target) return;
    const identifier = this.authorIdentifier;
    const hasReacted = (target.reactions[emoji] ?? []).includes(identifier);

    this.beginMutation();
    const previous = this._comments;
    this._lastError = null;
    this._comments = previous.map(c =>
      c.id === id
        ? {
            ...c,
            reactions: toggleReaction(
              c.reactions,
              emoji,
              identifier,
              !hasReacted,
            ),
          }
        : c,
    );

    const result = hasReacted
      ? await this.service.removeReaction(id, emoji, identifier)
      : await this.service.addReaction(id, emoji, identifier);
    if (result.ok) {
      const { id: reactedId, reactions } = result.value;
      this._comments = this._comments.map(c =>
        c.id === reactedId ? { ...c, reactions } : c,
      );
      // Only applying a reaction is tracked; un-reacting is not (per spec).
      if (!hasReacted) trackCommentReactionAdded({ ...this.eventBase, emoji });
    } else {
      this.rollback(previous, result.error, 'toggle reaction');
    }
    this.endMutation();
  }

  /** Delete a comment; a top-level comment cascades to its replies. */
  async deleteComment(id: string): Promise<void> {
    this.beginMutation();
    const previous = this._comments;
    // Captured before the optimistic filter so we can tag thread vs reply.
    const target = previous.find(c => c.id === id);
    const isTopLevel = target ? target.parentId == null : false;
    this._lastError = null;
    this._comments = previous.filter(c => c.id !== id && c.parentId !== id);

    const result = await this.service.deleteComment(id);
    if (!result.ok) this.rollback(previous, result.error, 'delete comment');
    else trackCommentDeleted({ ...this.eventBase, is_top_level: isTopLevel });
    this.endMutation();
  }

  // ---- Polling host -------------------------------------------------------

  /** Start the refetch interval (idempotent). Skips ticks while tab hidden. */
  startPolling(): void {
    if (this.pollTimer) return;
    this.pollTimer = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      void this.poll();
    }, POLL_INTERVAL_MS);
  }

  stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  /** Tear down timers; call on modal close / hook unmount. */
  dispose(): void {
    this.stopPolling();
    logger.info('Comment store disposed');
  }

  /**
   * Background refetch (the polling host). Unlike `refresh()`, it never touches
   * `_isLoading`/`_lastError` and never clobbers in-flight optimistic state: it
   * skips while a mutation is active, and discards a result whose fetch
   * straddled a mutation (via the generation counter) or an AR re-key.
   */
  private async poll(): Promise<void> {
    if (!this.videoId || this._activeMutations > 0) return;
    const videoId = this.videoId;
    const gen = this._mutationGeneration;
    const result = await this.service.listByClip(videoId);
    if (
      this._activeMutations > 0 ||
      this._mutationGeneration !== gen ||
      this.videoId !== videoId
    ) {
      return;
    }
    if (result.ok) this._comments = result.value;
  }

  // ---- Internals ----------------------------------------------------------

  private get authorIdentifier(): string {
    return this.author.id ?? this.author.name;
  }

  /** Shared analytics payload for every comment event. `content_type` is
   *  `'clip'` — the store is clip-scoped today. */
  private get eventBase(): {
    content_type: 'clip';
    video_id?: string;
    story_id?: string;
  } {
    return {
      content_type: 'clip',
      video_id: this.videoId,
      story_id: this.storyId,
    };
  }

  /**
   * Bracket an optimistic mutation. The generation bump lets an overlapping poll
   * detect that state changed under it; the active counter makes a poll skip
   * entirely while a mutation is in flight. Service calls return `Result` (never
   * throw), so a plain counter without try/finally is sufficient here.
   */
  private beginMutation(): void {
    this._mutationGeneration++;
    this._activeMutations++;
  }

  private endMutation(): void {
    this._activeMutations--;
  }

  private rollback(
    previous: Comment[],
    error: ServiceError,
    action: string,
  ): void {
    this._comments = previous;
    this._lastError = error;
    logger.warn(`Rolled back optimistic ${action}`, error);
  }
}

/** ISO timestamps sort lexicographically in chronological order. */
function byCreatedAtAsc(a: Comment, b: Comment): number {
  return a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0;
}
